/**
 * ONE URL FIELD PER BUTTON ON A CONTACT US BLOCK (2026-08-28, NEV).
 *
 * Reported as: *"The NEV contact us module has two buttons, but only one URL
 * option."* Both halves of that are true, and neither was a bug in the sense of
 * broken code:
 *
 *   • `ECH_ContactUsDefault` declares exactly ONE field, `ContactUrl` — so one box
 *     is all the panel could ever have offered. (And in the bundled pull it
 *     declares none at all, because its `@GUARDRAILS` header lives in nested slot
 *     content that `assetHtml()` never walks — see CONTACT-US-GUARDRAILS-ANSWER.)
 *   • the buttons a division actually sees come from ITS OWN Contact Us
 *     layoutblock, swapped in wholesale by `echIscPatch` at fill time. NEV's has
 *     two ("Call Us", "Email Us"); the generic fallback has one ("Contact Us").
 *
 * So the field count cannot be a constant — it is a property of the markup the
 * division gets. This module reads the buttons out of that markup and hands back
 * one url field for each, labelled with the button's own text.
 *
 * ── WHAT IS DELIBERATELY *NOT* HERE ──────────────────────────────────────────
 * No authored prose, no invented labels, no reference rows (Jack, 2026-08-28:
 * *"keeping the same boxes/format set up as other blocks… and not additional
 * text"*). Every field renders in the standard `.essec.echf` box, and each one's
 * PLACEHOLDER is that button's current live destination — which is the whole
 * explanation, in the place a requester is already looking. A `tel:` or `mailto:`
 * button reads as exactly that, so where the phone and email come from needs no
 * sentence.
 *
 * ── THE DURABLE FIX IS LAYNE'S, NOT OURS ─────────────────────────────────────
 * The right long-term shape is one url field per button declared in the block's
 * own `@GUARDRAILS`, at which point the registry's own fields win and this
 * derivation goes quiet on its own (it only ever fills a block that declares
 * nothing). Treat this as the bridge, not the answer.
 */
import type { FieldDescriptor } from "../types/blocks";

/** the ISC / Contact Us family — same test `echIscPatch` uses (echDyn.ts) */
export const ECH_CONTACT_RE = /^ECH_ContactUs/i;

export interface ContactButton {
  /** data key on the block: ContactUrl, ContactUrl2, ContactUrl3 … */
  k: string;
  /** the button's own visible text, e.g. "Call Us" */
  text: string;
  /** the href the markup carries today — the field's placeholder */
  href: string;
}

/** the first field keeps the DECLARED name so a block that declares `ContactUrl`
 *  (and `echIscPatch`, which reads it) keeps working unchanged */
const keyFor = (i: number): string => (i === 0 ? "ContactUrl" : "ContactUrl" + (i + 1));

/** strip tags/entities out of an anchor's inner html and collapse whitespace */
function anchorText(inner: string): string {
  return String(inner || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * COMMENTS AND <style> ARE NOT MARKUP (2026-08-28, measured on the live block).
 *
 * The real NEV block carries **35 HTML comments** and a `<style>` block, and one of
 * those comments contains the text `…tag Both links must match. -->`. A bare
 * `<a\b…>([\s\S]*?)</a>` scan matched an `<a` INSIDE a comment and swallowed
 * everything up to the next `</a>` — which produced a first "button" whose label was
 * **468 characters of CSS and comment text**, and ate the real "Call Us" button on
 * the way. Measured against asset 997261, not imagined.
 *
 * Masked rather than deleted: every masked region is replaced by spaces of the SAME
 * length, so character offsets in the returned string still line up with the
 * original. `contactRewrite` depends on that — it finds anchors in the masked copy
 * and rewrites hrefs in the real one.
 */
function maskNonMarkup(h: string): string {
  const blank = (m: string): string => " ".repeat(m.length);
  return h
    .replace(/<!--[\s\S]*?-->/g, blank)
    .replace(/<style\b[\s\S]*?<\/style\b[^>]*>/gi, blank)
    .replace(/<script\b[\s\S]*?<\/script\b[^>]*>/gi, blank);
}

/** a button label is a label, not a paragraph — cap it the way the panel can show it */
const LABEL_MAX = 60;

/**
 * A `{{Token}}` NEVER SHIPS (2026-09-10, Jack: "contact us default looks weird like this").
 *
 * `ECH_ContactUsDefault` is a Stensul-style template: its team photo and five
 * headshots are `<img src="{{Headshot1Url}}" alt="{{Headshot1Alt}}">` with a
 * `{{HeadshotSize}}` width. When a division's own Contact Us layoutblock swaps in,
 * none of that survives. When it does NOT — a division with no block, or a preview
 * whose `/sfmc-isc` call failed — the default shipped as placed, and the email
 * showed six broken-image icons each captioned with its own token text.
 *
 * Nothing here invents a photo. An image whose src is a token is removed (with an
 * anchor that wrapped only it), and any token still standing is blanked. Measured
 * on the live block: 6 tokenised images, 0 after.
 */
export function echStripUnresolved(html: unknown): string {
  let s = echDropTokenImages(html);
  if (s.indexOf("{{") < 0) return s;
  s = s.replace(/\{\{[A-Za-z0-9_.]+\}\}/g, "");
  return s;
}

/**
 * THE IMAGE HALF ALONE — runs in every ECH block's render (echRegistry), before any
 * fill has happened. Measured on prod 2026-09-10, step 1, no division picked yet: the
 * five `<img src="{{HeadshotNUrl}}">` rendered as 124px broken-image boxes in one
 * 718px row, which pushed the 600px table to 758px and clipped the whole email on
 * the right (Jack: "why are email preview and etc cut off"). A token in an img src is
 * never right in any block; text tokens are left alone here so a markup typo stays
 * visible, as the render's own rule says.
 */
export function echDropTokenImages(html: unknown): string {
  let s = String(html ?? "");
  if (s.indexOf("{{") < 0) return s;
  s = s.replace(/<img\b[^>]*\bsrc\s*=\s*"\{\{[^}]*\}\}"[^>]*>/gi, "");
  s = s.replace(/<img\b[^>]*\bsrc\s*=\s*'\{\{[^}]*\}\}'[^>]*>/gi, "");
  s = s.replace(/<a\b[^>]*>\s*<\/a>/gi, "");
  return s;
}

/**
 * Every linked button in the block, in document order.
 *
 * An anchor with no href, or the placeholder `href="#"`, still counts — that IS a
 * button the requester can see and therefore one they may need to point
 * somewhere. Anchors carrying no text at all are skipped: they are the wrapping
 * `<a>` around an image that several of Layne's blocks use, and giving one a field
 * would put a box in the panel for something nobody recognises as a button.
 */
export function contactButtons(html: unknown): ContactButton[] {
  const h = maskNonMarkup(String(html ?? ""));
  const out: ContactButton[] = [];
  try {
    const re = /<a\b([^>]*)>([\s\S]*?)<\/a>/gi;
    let m: RegExpExecArray | null;
    while ((m = re.exec(h))) {
      const text = anchorText(m[2]).slice(0, LABEL_MAX);
      if (!text) continue;
      const href = (/\bhref="([^"]*)"/i.exec(m[1]) || [])[1] || "";
      out.push({ k: keyFor(out.length), text, href });
      if (out.length >= 6) break; /* a contact block with 7 buttons is a data problem */
    }
  } catch { /* a malformed block must not take the panel down */ }
  return out;
}

/**
 * The panel's field list for a Contact Us block, derived from its markup.
 *
 * Labels follow the button, in the app's existing "<thing> — link" phrasing so the
 * section header reads the same as every other url field. `max` matches the
 * declared cap on `ContactUrl` (300) so the counter behaves identically.
 */
export function contactFields(html: unknown): FieldDescriptor[] {
  return contactButtons(html).map((b) => ({
    k: b.k,
    label: b.text + " — link",
    type: "url" as const,
  }));
}

/** true when THIS block should get the derived fields: a Contact Us block whose
 *  own guardrails declare nothing (a declaration always wins) */
export function contactDerives(echName: string, declaredCount: number): boolean {
  return ECH_CONTACT_RE.test(String(echName || "")) && declaredCount === 0;
}

/**
 * FIELDS FOR THE BUTTONS A DECLARATION DOESN'T COVER (2026-08-28).
 *
 * `contactDerives` above only fires on a block that declares NOTHING, which is the
 * state today — the backend hands the app the outer render, whose header it never
 * reads. **That state is temporary**, and its ending is a regression:
 *
 *   `fix/guardrails-in-slots` (BE) makes the header readable → `ECH_ContactUsDefault`
 *   declares exactly ONE `ContactUrl` → the derivation switches off → NEV is back to
 *   one box for two buttons, i.e. the bug it reported.
 *
 * Verified against the live assets 2026-08-28: NEV_Contact_Headshot ships **two**
 * buttons (Call Us → tel:, Email Us → mailto:) while AZH_Contact_NoPhoto ships one,
 * and the ~45 division blocks carry no header of their own by design — the default
 * block's header governs all of them. So a single declared field cannot be right for
 * every division, and the declaration is not wrong either: it is right for the one
 * button every variant shares.
 *
 * This is the seam between them. Layne's declaration keeps button 1 — her label,
 * cap, pattern and required flag all stand — and the markup supplies a field for each
 * button BEYOND what she declared. Nothing overrides her; it only extends past where
 * she stopped, per division, which is the only place the extra buttons exist.
 *
 * Returns [] whenever the declaration already covers every button, so a one-button
 * division sees exactly what she declared and nothing invented.
 */
export function contactExtraFields(echName: string, html: unknown, declaredUrlKeys: readonly string[]): FieldDescriptor[] {
  if (!ECH_CONTACT_RE.test(String(echName || ""))) return [];
  const declared = new Set(declaredUrlKeys.map((k) => String(k)));
  if (!declared.size) return [];                 /* the declares-nothing case is contactDerives' */
  return contactFields(html).filter((f) => !!f.k && !declared.has(f.k));
}

/**
 * A SCHEME-LESS `www.` ADDRESS IS THE REQUESTER'S ANSWER, NOT A NON-ANSWER
 * (2026-08-31, caught by the full end-to-end review build).
 *
 * The panel's url field accepted `www.lennar.com/contact` — it cleared the
 * required-field guardrail and the requester saw no error — and then `real()`
 * below silently rejected it at render, so the email SHIPPED the block's
 * placeholder `href="#"`. A value the panel accepts must either reach the email
 * or be refused in the panel; accepted-then-dropped is the worst of both.
 * Only the unmistakable `www.` prefix is normalized (to https); anything else
 * scheme-less stays untrusted, exactly as before.
 */
export const withScheme = (u: unknown): string => {
  const v = String(u ?? "").trim();
  return /^www\./i.test(v) ? "https://" + v : v;
};

/** a host is required, not just a scheme — the shipped `https://` placeholder is
 *  not an answer. Same test as `echIscHtml` / `echEffectiveLink`. */
const real = (u: unknown): boolean => /^(https?:\/\/[^\s/]|tel:|mailto:)/i.test(withScheme(u));

/**
 * Write each field's value onto ITS OWN button.
 *
 * `echIscHtml` rewrites only an href matching `/contact`, which on NEV's two-button
 * block matches neither `tel:` nor `mailto:` — so the one field it offered could
 * change nothing there. This maps field N onto anchor N instead, which is the only
 * mapping that stays true when the number of buttons is a division's property.
 *
 * An empty field leaves its button alone, so "empty means the division's own link
 * is used" holds per button rather than for the block as a whole.
 */
export function contactRewrite(html: unknown, data: Record<string, unknown>): string {
  const h = String(html ?? "");
  if (!h) return "";
  const btns = contactButtons(h);
  if (!btns.length) return h;
  /* ── OFFSETS, NOT AN ANCHOR COUNT (2026-08-28) ────────────────────────────────
     Counting `<a>` occurrences in the raw html and rewriting the Nth one breaks the
     moment a COMMENT contains an `<a` — the live NEV block has 35 comments, so the
     count and the rewrite disagreed and a typed URL could land on the wrong button
     or inside a comment. `maskNonMarkup` preserves character offsets, so the masked
     copy gives trustworthy positions for the real markup, and the edit is applied to
     the ORIGINAL string at those positions. */
  const masked = maskNonMarkup(h);
  const edits: { start: number; end: number; attrs: string; url: string }[] = [];
  let seen = 0;   /* button index, counting only anchors with text */
  const reScan = /<a\b([^>]*)>([\s\S]*?)<\/a>/gi;
  let s: RegExpExecArray | null;
  while ((s = reScan.exec(masked))) {
    if (!anchorText(s[2])) continue;      /* an image-wrapping link is not a button */
    const b = btns[seen];
    seen++;
    if (!b) continue;
    const v = withScheme(data[b.k]);      /* `www.…` is the requester's URL — see withScheme */
    if (!v || !real(v)) continue;         /* empty leaves ITS button alone */
    edits.push({ start: s.index, end: s.index + 2 + s[1].length + 1, attrs: s[1], url: v });
  }
  if (!edits.length) return h;
  let out = "";
  let cut = 0;
  for (const e of edits) {
    const esc = e.url.replace(/"/g, "&quot;");
    const tag = /\bhref="/i.test(e.attrs)
      ? "<a" + e.attrs.replace(/\bhref="[^"]*"/i, 'href="' + esc + '"') + ">"
      : "<a" + e.attrs + ' href="' + esc + '">';
    out += h.slice(cut, e.start) + tag;
    cut = e.end;
  }
  return out + h.slice(cut);
}
